import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { clearAuthToken, getAuthGeneration, getAuthToken } from '../utils/auth';
import { dispatchUnauthorizedAuthEvent } from '../utils/authSession';
import { API_BASE_URL } from '../config/apiConfig';
import { isWiseFriendApiRequest, protectCookieRequest } from './csrf';

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface AuthRequestSnapshot {
  generation: number;
  tokenPresent: boolean;
  userIdentity: string | null;
  hadAuthenticationState: boolean;
}

export interface WiseFriendAxiosRequestConfig extends AxiosRequestConfig {
  wiseFriendAuth?: {
    expectedAnonymousProfileProbe?: boolean;
    expectedLogoutTelemetryFailure?: boolean;
    requestSnapshot?: AuthRequestSnapshot;
  };
}

const getStoredUserIdentity = (): string | null => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) {
      return null;
    }

    try {
      const parsedUser = JSON.parse(rawUser) as { id?: unknown; username?: unknown };
      if (parsedUser.id !== undefined || parsedUser.username !== undefined) {
        return `${String(parsedUser.id ?? '')}:${String(parsedUser.username ?? '')}`;
      }
    } catch {
      // Keep malformed cached data distinguishable without exposing it in diagnostics.
    }

    return 'cached-user';
  } catch {
    return null;
  }
};

const getCurrentAuthSnapshot = (): AuthRequestSnapshot => {
  const tokenPresent = Boolean(getAuthToken());
  const userIdentity = getStoredUserIdentity();
  return {
    generation: getAuthGeneration(),
    tokenPresent,
    userIdentity,
    hadAuthenticationState: tokenPresent || userIdentity !== null,
  };
};

const isRequestSessionCurrent = (requestSnapshot: AuthRequestSnapshot) => {
  const currentSnapshot = getCurrentAuthSnapshot();
  return (
    requestSnapshot.generation === currentSnapshot.generation &&
    requestSnapshot.tokenPresent === currentSnapshot.tokenPresent &&
    requestSnapshot.userIdentity === currentSnapshot.userIdentity
  );
};

const isExpectedAnonymousProfileProbe = (
  config: WiseFriendAxiosRequestConfig | undefined,
  requestSnapshot: AuthRequestSnapshot | undefined,
) => {
  if (
    !config?.wiseFriendAuth?.expectedAnonymousProfileProbe ||
    !requestSnapshot ||
    requestSnapshot.hadAuthenticationState
  ) {
    return false;
  }

  const method = String(config.method ?? 'get').toLowerCase();
  const requestPath = String(config.url ?? '').split('?')[0];
  return method === 'get' && requestPath.endsWith('/api/user/me/');
};

const isPanelRequestTimeout = (error: any) => {
  const url = String(error?.config?.url || '');
  const isPanelRequest = url.includes('/questions-panel/') || url.includes('/messages-panel/');
  return isPanelRequest && (
    error?.code === 'ECONNABORTED' ||
    /timeout/i.test(String(error?.message || ''))
  );
};

const isExpectedLogoutTelemetryFailure = (
  config: WiseFriendAxiosRequestConfig | undefined,
) => {
  if (!config?.wiseFriendAuth?.expectedLogoutTelemetryFailure) {
    return false;
  }

  const method = String(config.method ?? '').toLowerCase();
  let requestUrl: URL;
  let apiBaseUrl: URL;
  try {
    apiBaseUrl = new URL(`${API_BASE_URL}/`, window.location.origin);
    requestUrl = new URL(String(config.url ?? ''), apiBaseUrl);
  } catch {
    return false;
  }

  let requestData = config.data as unknown;
  if (typeof requestData === 'string') {
    try {
      requestData = JSON.parse(requestData) as unknown;
    } catch {
      return false;
    }
  }
  const eventType = requestData && typeof requestData === 'object'
    ? (requestData as { event_type?: unknown }).event_type
    : undefined;

  return (
    method === 'post' &&
    requestUrl.origin === apiBaseUrl.origin &&
    requestUrl.pathname === '/api/events/' &&
    eventType === 'logout'
  );
};

const isProtectedSurveyRequest = (config: AxiosRequestConfig | undefined) => {
  const matches = (url: string) => {
    // Keep a known protected literal private even if URL/base resolution fails.
    if (/^\/api\/engagement-surveys(?:\/|[?#]|$)/.test(url)) return true;
    try {
      const path = new URL(url, window.location.origin).pathname;
      return /^\/api\/engagement-surveys(?:\/|$)/.test(path)
        || /^\/api\/engagement-surveys(?:\/|$)/.test(decodeURIComponent(path));
    } catch {
      return false;
    }
  };
  const url = String(config?.url ?? '');
  if (matches(url)) return true;
  const baseURL = config?.baseURL ?? API_BASE_URL;
  const absolute = /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
  if (typeof baseURL === 'string' && baseURL && (!absolute || config?.allowAbsoluteUrls === false)) {
    // Axios concatenates baseURL paths; URL(url, baseURL) would discard /api.
    // Do not invoke parameter serializers merely to classify a diagnostic.
    return matches(url ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : baseURL);
  }
  return false;
};

const diagnosticErrorCodes = new Set([
  'ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_CANCELED',
  'ERR_BAD_REQUEST', 'ERR_BAD_RESPONSE', 'ERR_INVALID_URL',
  'ERR_NOT_SUPPORT', 'ERR_FR_TOO_MANY_REDIRECTS',
]);

// Axios error objects include request headers, saved answers and response bodies.
// Protected-survey diagnostics keep only independently validated scalar fields.
const logHttpDiagnostic = (
  label: string,
  value: unknown,
  error: any,
  config: AxiosRequestConfig | undefined = error?.config ?? error?.response?.config,
) => {
  if (isProtectedSurveyRequest(config) || isProtectedSurveyRequest(error?.config) || isProtectedSurveyRequest(error?.response?.config)) {
    const rawStatus = error?.response?.status;
    console.error(label, {
      status: typeof rawStatus === 'number' && Number.isInteger(rawStatus) && rawStatus >= 100 && rawStatus <= 599 ? rawStatus : null,
      code: typeof error?.code === 'string' && diagnosticErrorCodes.has(error.code) ? error.code : null,
    });
    return;
  }
  console.error(label, value);
};

export const prepareAuthenticatedRequest = (config: InternalAxiosRequestConfig) => {
  const trackedConfig = config as typeof config & WiseFriendAxiosRequestConfig;
  const requestSnapshot = getCurrentAuthSnapshot();
  trackedConfig.wiseFriendAuth = {
    ...trackedConfig.wiseFriendAuth,
    requestSnapshot,
  };

  const token = getAuthToken();
  if (token && isWiseFriendApiRequest(trackedConfig)) {
    trackedConfig.headers.Authorization = `Token ${token}`;
  }
  protectCookieRequest(trackedConfig);
  return trackedConfig;
};

export const handleHttpRequestError = (error: any) => {
  logHttpDiagnostic('Request error:', error, error);
  return Promise.reject(error);
};

httpClient.interceptors.request.use(
  prepareAuthenticatedRequest,
  handleHttpRequestError,
  { synchronous: true },
);

export const handleHttpResponseError = (error: any) => {
  const status = error?.response?.status;
  const config = error?.config as WiseFriendAxiosRequestConfig | undefined;
  const expectedLogoutTelemetryFailure = isExpectedLogoutTelemetryFailure(config);
  if (status === 401) {
    const requestSnapshot = config?.wiseFriendAuth?.requestSnapshot;
    const expectedAnonymousProfileProbe = isExpectedAnonymousProfileProbe(
      config,
      requestSnapshot,
    );

    if (
      !expectedLogoutTelemetryFailure &&
      requestSnapshot?.hadAuthenticationState &&
      isRequestSessionCurrent(requestSnapshot)
    ) {
      clearAuthToken();
      try {
        localStorage.removeItem('user');
      } catch (storageError) {
        logHttpDiagnostic('Failed to clear user cache after 401:', storageError, storageError, config);
      }
      dispatchUnauthorizedAuthEvent({
        reason: 'session_expired',
        status,
        source: 'httpClient',
      });
    }

    if (!expectedAnonymousProfileProbe && !expectedLogoutTelemetryFailure) {
      logHttpDiagnostic('Response error:', error.response || error, error);
    }
  }

  if (status !== 401 && !isPanelRequestTimeout(error) && !expectedLogoutTelemetryFailure) {
    logHttpDiagnostic('Response error:', error.response || error, error);
  }
  return Promise.reject(error);
};

httpClient.interceptors.response.use(
  (response) => {
    const responseType = response.config?.responseType;
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      return response;
    }
    const contentTypeHeader = response.headers['content-type'];
    const contentType = Array.isArray(contentTypeHeader) ? contentTypeHeader.join(',') : contentTypeHeader;
    if (typeof contentType === 'string' && !contentType.includes('application/json')) {
      logHttpDiagnostic('Unexpected content type:', contentType, { response: { status: response.status } }, response.config);
      return Promise.reject(new Error('Unexpected response format'));
    }
    return response;
  },
  handleHttpResponseError,
);

export const request = async <T = AxiosResponse['data']>(
  config: AxiosRequestConfig,
): Promise<T> => {
  const response = await httpClient.request<T>(config);
  return response.data;
};

export default httpClient;
